import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';

const PermissionMatrix = ({ permissions, selectedPermissions = [], onChange }) => {
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    // Group permissions by module
    if (Array.isArray(permissions)) {
      // If already grouped
      const grouped = permissions.reduce((acc, perm) => {
        const module = perm.module || 'Other';
        if (!acc[module]) {
          acc[module] = [];
        }
        acc[module].push(perm);
        return acc;
      }, {});
      setGroupedPermissions(grouped);

      // Expand all by default
      const initialExpanded = {};
      Object.keys(grouped).forEach((module) => {
        initialExpanded[module] = true;
      });
      setExpanded(initialExpanded);
    } else if (typeof permissions === 'object') {
      // If permissions is already an object grouped by module
      setGroupedPermissions(permissions);

      // Expand all by default
      const initialExpanded = {};
      Object.keys(permissions).forEach((module) => {
        initialExpanded[module] = true;
      });
      setExpanded(initialExpanded);
    }
  }, [permissions]);

  const handleToggleExpand = (module) => {
    setExpanded({
      ...expanded,
      [module]: !expanded[module],
    });
  };

  const handlePermissionToggle = (permissionId) => {
    const newSelected = selectedPermissions.includes(permissionId)
      ? selectedPermissions.filter((id) => id !== permissionId)
      : [...selectedPermissions, permissionId];

    if (onChange) {
      onChange(newSelected);
    }
  };

  const handleModuleToggle = (modulePermissions) => {
    const modulePermissionIds = modulePermissions.map((p) => p.id);
    const allSelected = modulePermissionIds.every((id) =>
      selectedPermissions.includes(id)
    );

    let newSelected;
    if (allSelected) {
      // Deselect all in this module
      newSelected = selectedPermissions.filter(
        (id) => !modulePermissionIds.includes(id)
      );
    } else {
      // Select all in this module
      const toAdd = modulePermissionIds.filter(
        (id) => !selectedPermissions.includes(id)
      );
      newSelected = [...selectedPermissions, ...toAdd];
    }

    if (onChange) {
      onChange(newSelected);
    }
  };

  const isModuleFullySelected = (modulePermissions) => {
    return modulePermissions.every((p) => selectedPermissions.includes(p.id));
  };

  const isModulePartiallySelected = (modulePermissions) => {
    const selected = modulePermissions.some((p) =>
      selectedPermissions.includes(p.id)
    );
    const notAll = !modulePermissions.every((p) =>
      selectedPermissions.includes(p.id)
    );
    return selected && notAll;
  };

  const getModuleIcon = (module) => {
    const iconMap = {
      users: '👥',
      jobs: '💼',
      candidates: '👤',
      applications: '📝',
      assessments: '📋',
      roles: '🔐',
      dashboard: '📊',
      reports: '📈',
      settings: '⚙️',
    };
    return iconMap[module] || '📦';
  };

  if (Object.keys(groupedPermissions).length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No permissions available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Select permissions for this role
        </Typography>
        <Chip
          label={`${selectedPermissions.length} selected`}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Stack>

      {Object.entries(groupedPermissions).map(([module, modulePermissions]) => {
        const fullySelected = isModuleFullySelected(modulePermissions);
        const partiallySelected = isModulePartiallySelected(modulePermissions);

        return (
          <Accordion
            key={module}
            expanded={expanded[module]}
            onChange={() => handleToggleExpand(module)}
            sx={{ mb: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: fullySelected ? '#e8f5e9' : partiallySelected ? '#fff3e0' : '#f5f5f5',
                '&:hover': { bgcolor: fullySelected ? '#c8e6c9' : partiallySelected ? '#ffe0b2' : '#eeeeee' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                <Checkbox
                  checked={fullySelected}
                  indeterminate={partiallySelected}
                  onChange={() => handleModuleToggle(modulePermissions)}
                  onClick={(e) => e.stopPropagation()}
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckBoxIcon />}
                />
                <Typography sx={{ fontSize: 20 }}>{getModuleIcon(module)}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {module}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {modulePermissions.length} permissions
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${modulePermissions.filter((p) => selectedPermissions.includes(p.id)).length}/${modulePermissions.length}`}
                  color={fullySelected ? 'success' : partiallySelected ? 'warning' : 'default'}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: 'white' }}>
              <Box sx={{ pl: 2 }}>
                {modulePermissions.map((permission) => (
                  <Box key={permission.id} sx={{ py: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedPermissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {permission.display_name}
                          </Typography>
                          {permission.description && (
                            <Typography variant="caption" color="text.secondary">
                              {permission.description}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: '#666', fontFamily: 'monospace', mt: 0.5 }}
                          >
                            {permission.name}
                          </Typography>
                        </Box>
                      }
                    />
                    {modulePermissions.indexOf(permission) < modulePermissions.length - 1 && (
                      <Divider sx={{ ml: 4, mt: 1 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default PermissionMatrix;
